import { errorParametersFactory } from "./errors/errorParametersFactory.js";
import { csvLineParserFactory } from "./csvLineParserFactory.js";
import { InvalidOperationError } from "./errors/InvalidOperationError.js";
import { helpersFunc } from "config/helpers";

export var csvContactsParserFactory = function () {
  function areEqualIgnoreCase(first, second) {
    const result = first.toLowerCase() === second.toLowerCase();
    return result;
  }

  function createEmptyHeaderMapping() {
    const result = {
      LinkedInProfileIndex: -1,
      FirstNameIndex: -1,
      LastNameIndex: -1,
      CompanyIndex: -1,
      TitleIndex: -1,
      CountryIndex: -1,
      CustomField1: -1,
      CustomField2: -1,
      CustomField3: -1,
    };
    return result;
  }

  function getHeaderMapping(fields) {
    const result = createEmptyHeaderMapping();
    for (let i = 0; i <= fields.length - 1; i++) {
      const currentField = fields[i];
      if (areEqualIgnoreCase(currentField, "LinkedIn Profile")) {
        result.LinkedInProfileIndex = i;
      }

      if (areEqualIgnoreCase(currentField, "First Name")) {
        result.FirstNameIndex = i;
      }

      if (areEqualIgnoreCase(currentField, "Last Name")) {
        result.LastNameIndex = i;
      }

      if (areEqualIgnoreCase(currentField, "Company Name")) {
        result.CompanyIndex = i;
      }

      if (areEqualIgnoreCase(currentField, "Position Title")) {
        result.TitleIndex = i;
      }

      if (areEqualIgnoreCase(currentField, "Country")) {
        result.CountryIndex = i;
      }

      if (areEqualIgnoreCase(currentField, "Custom Field 1")) {
        result.CustomField1 = i;
      }

      if (areEqualIgnoreCase(currentField, "Custom Field 2")) {
        result.CustomField2 = i;
      }

      if (areEqualIgnoreCase(currentField, "Custom Field 3")) {
        result.CustomField3 = i;
      }
    }

    return result;
  }

  function extractLinkedInIds(source, metadata) {
    if (!source) {
      source = "";
    }

    const linkedInProfiles = [
      ...source.matchAll(
        /(?:\s|)?linkedin\.com\/in\/(?<identifier>.[^\/\s,;:*."'#@$?+<>\]\[]*)/gim
      ),
    ];
    const salesNavigatorProfiles = [
      ...source.matchAll(
        /(?:\s|)?linkedin\.com\/sales\/lead\/(?<identifier>.[^\/\s,;:*."'#@$?+<>\]\[]*)/gim
      ),
    ];
    const profiles = linkedInProfiles.concat(salesNavigatorProfiles);
    const rawResult = [];
    for (const match of profiles) {
      try {
        const identifier = decodeURI(match.groups.identifier);
        rawResult.push(identifier);
      } catch (error) {
        const parameters = errorParametersFactory.create(error);
        parameters.identifier = match.groups.identifier;
        parameters.csvImport = true;
        parameters.metadata = metadata;
        helpersFunc.logEnjectServiceCatch(parameters);
      }
    }
    const result = [...new Set(rawResult)];
    return result;
  }

  function createContact(
    publicIdentifier,
    firstName,
    lastName,
    fullName,
    companyName,
    title,
    country,
    customField1,
    customField2,
    customField3
  ) {
    const result = {
      miniProfile: {
        publicIdentifier: publicIdentifier,
        fullName: fullName,
        firstName: firstName,
        lastName: lastName,
      },
      country: country,
      location: country,
      lastJob: null,
      // lastJob: {
      //     companyName: "Svitlo Systems",
      //     title: "Support Engineer"
      // }
      customField1: customField1,
      customField2: customField2,
      customField3: customField3,
    };

    if (companyName || title) {
      result.lastJob = {
        companyName: companyName,
        title: title,
      };
    }

    return result;
  }

  function extractContact(headerMapping, fields, metadata) {
    let result = null;
    let publicIdentifier = null;
    if (fields.length > headerMapping.LinkedInProfileIndex) {
      const linkedInProfile = fields[headerMapping.LinkedInProfileIndex];
      const ids = extractLinkedInIds(linkedInProfile, metadata);
      if (ids.length > 0) {
        publicIdentifier = ids[0];
      }
    }

    if (publicIdentifier) {
      let firstName = null;
      let lastName = null;
      let company = null;
      let title = null;
      let fullName = null;
      let country = null;
      let customField1 = null;
      let customField2 = null;
      let customField3 = null;
      if (
        headerMapping.FirstNameIndex &&
        fields.length > headerMapping.FirstNameIndex
      ) {
        firstName = fields[headerMapping.FirstNameIndex];
      }

      if (
        headerMapping.LastNameIndex &&
        fields.length > headerMapping.LastNameIndex
      ) {
        lastName = fields[headerMapping.LastNameIndex];
      }

      if (
        headerMapping.CompanyIndex &&
        fields.length > headerMapping.CompanyIndex
      ) {
        company = fields[headerMapping.CompanyIndex];
      }

      if (
        headerMapping.TitleIndex &&
        fields.length > headerMapping.TitleIndex
      ) {
        title = fields[headerMapping.TitleIndex];
      }

      if (
        headerMapping.CountryIndex &&
        fields.length > headerMapping.CountryIndex
      ) {
        country = fields[headerMapping.CountryIndex];
      }

      if (
        headerMapping.CustomField1 &&
        fields.length > headerMapping.CustomField1
      ) {
        customField1 = fields[headerMapping.CustomField1];
      }

      if (
        headerMapping.CustomField2 &&
        fields.length > headerMapping.CustomField2
      ) {
        customField2 = fields[headerMapping.CustomField2];
      }

      if (
        headerMapping.CustomField3 &&
        fields.length > headerMapping.CustomField3
      ) {
        customField3 = fields[headerMapping.CustomField3];
      }

      if (firstName || lastName) {
        if (firstName) {
          fullName = firstName;
        }

        if (lastName) {
          if (fullName) {
            fullName += " ";
          }

          fullName += lastName;
        }

        fullName = fullName?.trim();
      } else {
        fullName = publicIdentifier;
      }
      result = createContact(
        publicIdentifier,
        firstName,
        lastName,
        fullName,
        company,
        title,
        country,
        customField1,
        customField2,
        customField3
      );
    }

    return result;
  }

  function parseLinkedInContacts(source, metadata) {
    const ids = extractLinkedInIds(source, metadata);
    const result = [];
    for (const id of ids) {
      const contact = createContact(
        id,
        null,
        null,
        id,
        null,
        null,
        null,
        null,
        null,
        null
      );
      result.push(contact);
    }
    return result;
  }

  // explicitlyDefinedSeparator is not yet support, only parsed
  function getHeaderDefinition(headerLine, explicitlyDefinedSeparator) {
    const delimiters = [";", ",", "\t"];
    const fieldsCount = getFieldsCount();
    const normalizedHeaderLine = normalizeSource(headerLine);
    for (const delimiter of delimiters) {
      const parser = csvLineParserFactory(normalizedHeaderLine, delimiter);
      const response = parser.parse(0, fieldsCount);
      console.warn(response);
      const mapping = getHeaderMapping(response.fields);
      if (isValidHeaderMapping(mapping)) {
        const result = {
          delimiter: delimiter,
          mapping: mapping,
        };
        return result;
      }
    }

    throw new InvalidOperationError("This file is not Snaily csv file");
  }

  function getFieldsCount() {
    const result = Object.keys(createEmptyHeaderMapping()).length;
    return result;
  }

  function normalizeSource(source) {
    const result = !source ? "" : source.trim();
    return result;
  }

  function parseFullLinkedInContacts(source, metadata) {
    const allLines = source.replaceAll("\r\n", "\n").split("\n");

    const result = [];
    if (allLines.length > 0) {
      let headerLine = allLines[0];
      let skipLines = 1;

      let definedCsvDelimiter = null;
      const separatorPrefix = "sep=";
      if (headerLine.toLowerCase().startsWith(separatorPrefix)) {
        definedCsvDelimiter = headerLine.substring(separatorPrefix.length);
        headerLine = allLines[1];
        skipLines = 2;
      }

      const headerDefinition = getHeaderDefinition(
        headerLine,
        definedCsvDelimiter
      );
      const recordsText = allLines.slice(skipLines).join("\n");
      const normalizedRecordsText = normalizeSource(recordsText);
      let currentIndex = 0;
      const fieldsCount = getFieldsCount();
      do {
        const parser = csvLineParserFactory(
          normalizedRecordsText,
          headerDefinition.delimiter
        );
        const response = parser.parse(currentIndex, fieldsCount);
        const contact = extractContact(
          headerDefinition.mapping,
          response.fields,
          metadata
        );
        if (contact) {
          result.push(contact);
        } else {
          // can't break here, the contact might be invalid and has broken profile url or it is missed then contact could be null
          // break;
        }
        currentIndex = response.index + 1;
      } while (currentIndex < normalizedRecordsText.length - 1);
    } else {
      throw new InvalidOperationError("This file is not Snaily csv file");
    }

    return result;
  }

  function isValidHeaderMapping(mapping) {
    const result = !(
      mapping.LinkedInProfileIndex < 0 ||
      mapping.FirstNameIndex < 0 ||
      mapping.LastNameIndex < 0 ||
      mapping.CompanyIndex < 0 ||
      mapping.TitleIndex < 0 ||
      mapping.CountryIndex < 0 ||
      mapping.CustomField1 < 0 ||
      mapping.CustomField2 < 0 ||
      mapping.CustomField3 < 0
    );
    return result;
  }

  const result = {
    parseFullLinkedInContacts: parseFullLinkedInContacts,
    parseLinkedInContacts: parseLinkedInContacts,
  };

  return result;
};
